import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Board from 'react-trello';
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { useHistory } from 'react-router-dom';
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { Badge, Tooltip, Typography, Button, TextField, Box, FormControl, InputLabel, Select, MenuItem, Chip, CircularProgress, InputAdornment } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import KanbanLaneManager from "../../components/KanbanLaneManager";
import "./kanbanLayout.css";
import { format, isSameDay, parseISO } from "date-fns";
import { Can } from "../../components/Can";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: theme.spacing(2),
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  kanbanContainer: {
    width: "100%",
    maxWidth: "none",
    minWidth: 0,
    margin: 0,
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: 'bold',
    borderRadius: 3,
    fontSize: "0.6em",
  },
  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    marginLeft: "auto",
    color: theme.palette.text.secondary,
  },
  lastMessageTimeUnread: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    color: theme.palette.success.main,
    fontWeight: "bold",
    marginLeft: "auto"
  },
  cardButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  dateInput: {
    marginRight: theme.spacing(2),
  },
  userSelect: {
    minWidth: 200,
    marginRight: theme.spacing(2),
  },
  loadingIndicator: {
    marginLeft: theme.spacing(2),
  },
  userChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    '& .MuiChip-root': {
      height: 20,
      fontSize: '0.7rem',
    }
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme(); // Obter o tema atual
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketNot, setTicketNot] = useState(0);
  const [file, setFile] = useState({ lanes: [] });
  // Sem período obrigatório: atendimentos abertos antigos não podem sumir
  // somente porque o usuário abriu o Kanban em um novo dia.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const jsonString = (user.queues || []).map(queue => queue.id);
  const canSeeAllUsers = user.profile === "admin" && user.allUserChat === "enabled";

  useEffect(() => {
    fetchTags();
    if (canSeeAllUsers) {
      fetchUsers();
    }
  }, [user.id, user.companyId, canSeeAllUsers]);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users/list");
      setUsers(response.data || []);
    } catch (error) {
      console.log("Erro ao buscar usuários:", error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get("/tag/kanban/");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { queueIds: JSON.stringify(jsonString) };
      if (startDate && endDate) {
        params.dateStart = startDate;
        params.dateEnd = endDate;
      }
      if (canSeeAllUsers && selectedUserIds.length > 0) {
        params.selectedUserIds = JSON.stringify(selectedUserIds);
      }

      // O backend devolve páginas para evitar joins duplicados e limites
      // silenciosos. Buscar as demais páginas antes de montar as colunas.
      const allTickets = [];
      let pageNumber = 1;
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get("/ticket/kanban", {
          params: { ...params, pageNumber: String(pageNumber) }
        });
        allTickets.push(...data.tickets);
        hasMore = data.hasMore;
        pageNumber += 1;
      }
      setTickets(allTickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // useEffect para busca automática com debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTickets();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [startDate, endDate, selectedUserIds, user.id, user.companyId, canSeeAllUsers]);

  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    // Content-free invalidation after a ticket leaves the user's access.
    socket.on(`company-${companyId}-ticketInvalidate`, fetchTickets);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
      socket.off(`company-${companyId}-ticketInvalidate`, fetchTickets);
    };
  }, [socket, user.companyId, startDate, endDate, selectedUserIds, canSeeAllUsers]);

  const handleUserSelectionChange = (event) => {
    const value = event.target.value;
    
    // Se selecionou "Todos os usuários" (valor vazio), limpa outras seleções
    if (value.includes("")) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(value);
    }
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle", fontSize: "16px" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle", fontSize: "16px" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle", fontSize: "16px" }} />
      default:
        return "error";
    }
  };

  const popularCards = () => {
    // Etiquetas comuns não definem a etapa. Preservar a ordenação por tag
    // evita que o ticket apareça duas vezes se possuir mais de uma lane.
    const laneForTicket = (ticket) => {
      const ticketTagIds = (ticket.tags || []).map(tag => tag.id);
      const stage = tags.find(tag => ticketTagIds.includes(tag.id));
      return stage ? String(stage.id) : "lane0";
    };
    const term = searchTerm.trim().toLocaleLowerCase("pt-BR");
    const visibleTickets = !term ? tickets : tickets.filter(ticket => [
      ticket.id, ticket.contact?.name, ticket.contact?.number,
      ticket.lastMessage, ticket.user?.name
    ].some(value => String(value || "").toLocaleLowerCase("pt-BR").includes(term)));

    const createCard = (ticket) => ({
      id: String(ticket.id),
      label: `Ticket nº ${ticket.id}`,
      title: (
        <div className="ag-kanban-card-title">
          <Tooltip title={ticket.whatsapp?.name || "Conexão"}>
            <span>{IconChannel(ticket.channel)}</span>
          </Tooltip>
          <span title={ticket.contact?.name || "Contato"} className="ag-kanban-contact-name">
            {ticket.contact?.name || ticket.contact?.number || "Contato"}
          </span>
        </div>
      ),
      description: (
        <div className="ag-kanban-card-content">
          <div className="ag-kanban-card-meta">
            <span>{ticket.contact?.number || ""}</span>
            <Typography
              className={Number(ticket.unreadMessages) > 0 ? classes.lastMessageTimeUnread : classes.lastMessageTime}
              component="span"
              variant="body2"
            >
              {isSameDay(parseISO(ticket.updatedAt), new Date())
                ? format(parseISO(ticket.updatedAt), "HH:mm")
                : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
            </Typography>
          </div>
          <div className="ag-kanban-last-message" title={ticket.lastMessage || ""}>
            {ticket.lastMessage || "Sem mensagem recente"}
          </div>
          <div className="ag-kanban-card-footer">
            {ticket?.user ? (
              <Badge style={{ backgroundColor: "#171d31" }} className={classes.connectionTag}>
                {ticket.user.name.toUpperCase()}
              </Badge>
            ) : <span />}
            <Button
              size="small"
              variant="contained"
              className={classes.cardButton}
              onClick={() => handleCardClick(ticket.uuid)}
            >Ver ticket</Button>
          </div>
        </div>
      ),
      draggable: true,
      href: `/tickets/${ticket.uuid}`
    });

    const laneDefinitions = [
      { id: "lane0", name: i18n.t("tagsKanban.laneDefault"), color: "#e4e4e4" },
      ...tags.map(tag => ({ id: String(tag.id), name: tag.name, color: tag.color || "#dddddd" }))
    ];
    const lanes = laneDefinitions.map(lane => {
      const laneTickets = visibleTickets.filter(ticket => laneForTicket(ticket) === lane.id);
      return {
        id: lane.id,
        title: lane.name,
        label: String(laneTickets.length),
        labelStyle: { color: "inherit" },
        cards: laneTickets.map(createCard),
        style: {
          "--ag-lane-color": lane.color,
          "--ag-lane-text": (() => {
            const hex = String(lane.color).replace("#", "");
            if (!/^[0-9a-f]{6}$/i.test(hex)) return "#ffffff";
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return ((r * 299 + g * 587 + b * 114) / 1000) > 162 ? "#202938" : "#ffffff";
          })(),
          backgroundColor: "#f5f7fa",
          color: "#24324a"
        }
      };
    });
    setFile({ lanes });
  };

  const handleCardClick = (uuid) => {
    history.push('/tickets/' + uuid);
  };

  useEffect(() => {
    popularCards();
  }, [tags, tickets, searchTerm]);

  const handleCardMove = async (fromLaneId, toLaneId, cardId) => {
    try {
      const tagId = toLaneId === "lane0" ? 0 : Number(toLaneId);
      await api.patch(`/ticket-tags/${cardId}/move`, { tagId });
      await fetchTickets();
    } catch (err) {
      toast.error("Não foi possível mover o ticket. Verifique suas permissões.");
      await fetchTickets();
    }
  };

  const handleAddConnectionClick = () => {
    setManagerOpen(true);
  };

  // Mesma contagem exibida nas colunas; não revela tickets fora da
  // visualização autorizada pelo backend.
  const laneCounts = Object.fromEntries(
    (file.lanes || []).map(lane => [lane.id, lane.cards.length])
  );

  return (
    <div className={`${classes.root} ag-kanban-page`}>
      <div className="ag-kanban-filters">
        <div className="ag-kanban-filter-fields">
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            className={classes.dateInput}
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            className={classes.dateInput}
          />
          {canSeeAllUsers && (
            <FormControl variant="outlined" className={classes.userSelect}>
              <InputLabel>Filtrar Usuários</InputLabel>
              <Select
                multiple
                value={selectedUserIds}
                onChange={handleUserSelectionChange}
                label="Filtrar Usuários"
                renderValue={(selected) => (
                  <div className={classes.userChips}>
                    {selected.length === 0 ? <span style={{ color: '#666' }}>Todos</span> :
                      selected.map(userId => {
                        const selectedUser = users.find(u => u.id === userId);
                        return <Chip key={userId} label={selectedUser?.name || `ID: ${userId}`} size="small" color="primary" />;
                      })}
                  </div>
                )}
              >
                <MenuItem value=""><em>Todos os usuários</em></MenuItem>
                {users.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TextField
            className="ag-kanban-search"
            variant="outlined"
            placeholder="Pesquisar tickets..."
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            inputProps={{ "aria-label": "Pesquisar tickets no Kanban" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          {loading && <CircularProgress size={24} className={classes.loadingIndicator} />}
        </div>
        <Can role={user.profile} perform="dashboard:view" yes={() => (
          <Button variant="contained" color="primary" onClick={handleAddConnectionClick}>
            + Adicionar colunas
          </Button>
        )} />
      </div>
      <div className={`${classes.kanbanContainer} ag-kanban-board`}>
        <Board
          data={file}
          onCardMoveAcrossLanes={handleCardMove}
          style={{ backgroundColor: 'transparent' }}
        />
      </div>
      {managerOpen && (
        <KanbanLaneManager
          open={managerOpen}
          onClose={() => setManagerOpen(false)}
          onChange={fetchTags}
          laneCounts={laneCounts}
        />
      )}
    </div>
  );
};

export default Kanban;
